import { type ShipmentOrderInputHistoryDetailEntity } from '@/libs/api'

/**
 * `buildHistoryRestoreFieldValues` が受け取る履歴データの契約。
 * `ShipmentOrderInputHistorySelect` の `onHistorySelect` が渡す
 * `ShipmentOrderInputHistoryDetailEntity` と同一。
 */
export type ShipmentOrderHistoryLike = ShipmentOrderInputHistoryDetailEntity

/**
 * 履歴から復元するフィールドの値。キーは namePrefix を付けない素のフィールド名。
 * `additionalCharges` は APC マスタの非同期取得を伴うため、この純関数には含めない
 * （呼び出し側で個別に組み立てる）。
 */
export type HistoryRestoreFieldValues = Record<string, unknown>

/**
 * 配送依頼履歴 (`ShipmentOrderInputHistoryDetailEntity`) から、
 * フォームへ復元するフィールド値のマップを組み立てる。
 *
 * `?? null` / `?? ''` のフォールバックは、履歴セレクトの復元がどのフォームでも
 * 同じ値になるよう固定する（未設定の備考は空文字、それ以外は null）。
 */
export const buildHistoryRestoreFieldValues = (
  history: ShipmentOrderHistoryLike
): HistoryRestoreFieldValues => ({
  originSiteId: history.originSiteId,
  destSiteId: history.destSiteId,
  // 発着地備考・経由地は地点とセットで復元する（地点だけ差し替わって直前の地点の
  // 備考が残る／保存した経由地が消えるのを防ぐ）。
  originNote: history.originNote ?? null,
  destNote: history.destNote ?? null,
  // #4733: 絞り込みエリアも地点とセットで復元する（過去の履歴はエリアを持たないため
  // null = 未選択に落ちる）
  originAreaId: history.originAreaId ?? null,
  destAreaId: history.destAreaId ?? null,
  waypoints: (history.waypoints ?? []).map((waypoint) => ({
    siteId: waypoint.siteId,
    note: waypoint.note ?? null,
    areaId: waypoint.areaId ?? null,
    workType: waypoint.workType ?? 'OTHER'
  })),
  shipmentCategoryId: history.shipmentCategoryId,
  packageCategoryId: history.packageCategoryId,
  customerId: history.customerId ?? null,
  truckModelId: history.truckModelId ?? null,
  partnerId: history.partnerId ?? null,
  income: history.income ?? null,
  incomeTaxCategoryId: history.incomeTaxCategoryId ?? null,
  incomeIsTaxInclusive: history.incomeIsTaxInclusive ?? null,
  incomeRoundType: history.incomeRoundType ?? null,
  note: history.note ?? '',
  customKey1: history.customKey1 ?? null,
  customKey2: history.customKey2 ?? null,
  customKey3: history.customKey3 ?? null,
  customKey4: history.customKey4 ?? null,
  customKey5: history.customKey5 ?? null,
  historyId: history.id,
  historyName: history.name ?? null,
  departureTimeRestrictionStart: history.departureTimeRestrictionStart ?? null,
  departureTimeRestrictionEnd: history.departureTimeRestrictionEnd ?? null,
  arrivalTimeRestrictionStart: history.arrivalTimeRestrictionStart ?? null,
  arrivalTimeRestrictionEnd: history.arrivalTimeRestrictionEnd ?? null,
  // 荷役作業の委託有無と予定作業時間（#4740 → #4938 指摘対応で履歴からの復元に追加）
  isLoadingWorkOutsourced: history.isLoadingWorkOutsourced,
  loadingWorkMinutes: history.loadingWorkMinutes ?? null,
  isUnloadingWorkOutsourced: history.isUnloadingWorkOutsourced,
  unloadingWorkMinutes: history.unloadingWorkMinutes ?? null,
  // 荷役作業の備考（#4893）。備考だけ履歴から戻らない状態にしない
  loadingWorkNote: history.loadingWorkNote ?? null,
  unloadingWorkNote: history.unloadingWorkNote ?? null,
  items: history.items.map((item) => ({
    ...item,
    weight: item.weight != null ? item.weight : null
  }))
})
